import React, { ReactNode } from 'react';
import moment from 'moment';
import { unparse as convertToCSV } from 'papaparse/papaparse.min';
import {
    downloadCSV,
    TextField,
    DateField,
    ShowButton,
    TextInput,
    Filter,
    ReferenceInput,
    SelectInput,
    AutocompleteInput,
    DateInput,
    SelectArrayInput,
    FunctionField,
} from 'react-admin';
import CustomizableGrid from '../components/CustomizableGrid';
import { ColumnConfig } from '../hooks/useColumnReorder';

import MoneyField from '../components/MoneyField';
import CustomSearchInput from '../components/CustomSearchInput';
import BoxitChipField from '../components/BoxitChipField';
import BoxitPagination from '../components/BoxitPagination';
import ChipField from '../components/ChipField';
import { InvoiceStatus, InvoiceType } from '../constants';
import InvoiceNo from './components/InvoiceNo';
import { formatMoney } from '../util/formatMoney';
import InvoiceUserField from './components/InvoiceUserField';
import InvoicePartnerField from './components/InvoicePartnerField';
import InvoiceCountryField from './components/InvoiceCountryField';

const exporter = (records, fetchRelatedRecords) => {
    fetchRelatedRecords(records, 'countryId', 'country').then(countries => {
        const data = records.map(record => ({
            ...record,
            amountBeforeTax:
                record.type === 'creditNote'
                    ? -formatMoney(record.amountBeforeTax, record.currency)
                    : formatMoney(record.amountBeforeTax, record.currency),
            taxAmount:
                record.type === 'creditNote'
                    ? -formatMoney(record.taxAmount, record.currency)
                    : formatMoney(record.taxAmount, record.currency),
            amount:
                record.type === 'creditNote'
                    ? -formatMoney(record.amount, record.currency)
                    : formatMoney(record.amount, record.currency),
            amountPaid:
                record.type === 'creditNote'
                    ? formatMoney(record.amountPaid, record.currency)
                    : formatMoney(record.amountPaid, record.currency),
            country:
                countries[record.countryId] && countries[record.countryId].name,
            name: record.user.name,
            email: record.user.email,
        }));
        const csv = convertToCSV({
            data,
            fields: [
                'id',
                'date',
                'dueDate',
                'status',
                'currency',
                'amountBeforeTax',
                'taxAmount',
                'amount',
                'amountPaid',
                'paidAt',
                'name',
                'email',
                'country',
            ],
        });
        downloadCSV(csv, `Invoices_${Date.now()}`);
    });
};

const InvoiceFilter = props => (
    <Filter variant="outlined" {...props}>
        <CustomSearchInput alwaysOn />

        <ReferenceInput
            source="partnerId"
            reference="partners"
            resource="partners"
            label="Partner"
        >
            <SelectInput />
        </ReferenceInput>

        <SelectArrayInput
            style={{ minWidth: '10rem' }}
            source="status"
            choices={Object.keys(InvoiceStatus).map(c => ({ id: c, name: c }))}
        />

        <SelectInput
            source="type"
            choices={Object.keys(InvoiceType).map(c => ({ id: c, name: c }))}
        />

        <ReferenceInput
            source="userId"
            reference="users"
            resource="users"
            label="Customer"
        >
            <AutocompleteInput
                shouldRenderSuggestions={val => val.trim().length > 2}
                optionText={user => (user.name ? user.name : user.email)}
            />
        </ReferenceInput>

        <ReferenceInput
            source="countryId"
            reference="country"
            resource="country"
            label="Country"
        >
            <SelectInput />
        </ReferenceInput>

        <DateInput
            source="date[$gte]"
            label="Date From"
            parse={v => v && moment(v).format('YYYY-MM-DD')}
        />
        <DateInput
            source="date[$lte]"
            label="Date To"
            parse={v => v && moment(v).format('YYYY-MM-DD')}
        />

        <DateInput
            source="dueDate[$gte]"
            label="Due Date From"
            parse={v => v && moment(v).format('YYYY-MM-DD')}
        />
        <DateInput
            source="dueDate[$lte]"
            label="Due Date To"
            parse={v => v && moment(v).format('YYYY-MM-DD')}
        />
    </Filter>
);

const InvoiceList = ({ permissions = [], ...props }) => {
    // Define default columns configuration
    const defaultColumns: ColumnConfig[] = [
        { id: 'serial', label: 'Serial', visible: true, order: 0 },
        { id: 'id', label: 'ID', visible: true, order: 1 },
        { id: 'invoiceNo', label: 'No', visible: true, order: 2 },
        { id: 'createdAt', label: 'Created', visible: true, order: 3 },
        { id: 'date', label: 'Date', visible: true, order: 4 },
        { id: 'dueDate', label: 'Due Date', visible: true, order: 5 },
        { id: 'partner', label: 'Partner', visible: true, order: 6 },
        { id: 'customer', label: 'Customer', visible: true, order: 7 },
        { id: 'type', label: 'Type', visible: true, order: 8 },
        { id: 'amount', label: 'Amount', visible: true, order: 9 },
        { id: 'paid', label: 'Paid', visible: true, order: 10 },
        { id: 'status', label: 'Status', visible: true, order: 11 },
        { id: 'paidAt', label: 'Paid On', visible: true, order: 12 },
        { id: 'country', label: 'Country', visible: true, order: 13 },
        { id: 'show', label: 'Show', visible: true, order: 14 },
    ];

    // Function to render column based on column ID
    const renderColumn = (columnId: string): ReactNode => {
        switch (columnId) {
            case 'serial':
                return (
                    <TextField
                        key={columnId}
                        source="serial"
                        sortable={false}
                    />
                );
            case 'id':
                return <TextField key={columnId} source="id" />;
            case 'invoiceNo':
                return <InvoiceNo key={columnId} label="No" />;
            case 'createdAt':
                return (
                    <DateField
                        key={columnId}
                        locales="en-IN"
                        source="createdAt"
                        showTime
                        label="Created"
                    />
                );
            case 'date':
                return (
                    <DateField key={columnId} locales="en-IN" source="date" />
                );
            case 'dueDate':
                return (
                    <DateField
                        key={columnId}
                        locales="en-IN"
                        source="dueDate"
                        label="Due Date"
                    />
                );
            case 'partner':
                return (
                    <InvoicePartnerField
                        key={columnId}
                        label="Partner"
                        link="show"
                    />
                );
            case 'customer':
                return (
                    <InvoiceUserField
                        key={columnId}
                        label="Customer"
                        link="show"
                    />
                );
            case 'type':
                return <ChipField key={columnId} source="type" />;
            case 'amount':
                return (
                    <FunctionField
                        key={columnId}
                        label="Amount"
                        render={record =>
                            record.type === 'creditNote' ? (
                                <>
                                    <small style={{ display: 'block' }}>
                                        {record.currency}
                                    </small>
                                    -
                                    {formatMoney(
                                        record.amount,
                                        record.currency
                                    )}
                                </>
                            ) : (
                                <>
                                    <small style={{ display: 'block' }}>
                                        {record.currency}
                                    </small>
                                    {formatMoney(
                                        record.amount,
                                        record.currency
                                    )}
                                </>
                            )
                        }
                    />
                );
            case 'paid':
                return (
                    <FunctionField
                        key={columnId}
                        label="Paid"
                        render={record =>
                            record.type === 'creditNote' ? (
                                <>
                                    <small style={{ display: 'block' }}>
                                        {record.currency}
                                    </small>
                                    {formatMoney(
                                        record.amountPaid,
                                        record.currency
                                    )}
                                </>
                            ) : (
                                <>
                                    <small style={{ display: 'block' }}>
                                        {record.currency}
                                    </small>
                                    {formatMoney(
                                        record.amountPaid,
                                        record.currency
                                    )}
                                </>
                            )
                        }
                    />
                );
            case 'status':
                return (
                    <BoxitChipField
                        key={columnId}
                        record
                        source="status"
                        list="invoice"
                    />
                );
            case 'paidAt':
                return (
                    <DateField
                        key={columnId}
                        locales="en-IN"
                        source="paidAt"
                        label="Paid On"
                    />
                );
            case 'country':
                return <InvoiceCountryField key={columnId} />;
            case 'show':
                return <ShowButton key={columnId} />;
            default:
                return null;
        }
    };

    // Ensure we have all required props, but avoid duplicates with what might be in props
    const combinedProps = {
        title: 'Invoices',
        permissions,
        defaultColumns,
        storageKey: 'invoiceListColumns',
        renderColumn,
        bulkActionButtons: false,
        exporter: exporter,
        filtersComponent: <InvoiceFilter />,
        paginationComponent: <BoxitPagination />,
        sort: { field: 'id', order: 'DESC' },
        ...props,
        // Ensure these are set if not in props
        resource: props.resource || 'invoices',
        location: props.location || { search: '' },
        hasShow: props.hasShow !== undefined ? props.hasShow : true,
    };

    return <CustomizableGrid {...combinedProps} />;
};

InvoiceList.defaultProps = {
    resource: 'invoices',
    location: { search: '' },
};

export default InvoiceList;
